import React, { useEffect } from 'react';
import {
  Form,
  FormInstance,
  DateSerialized,
  SelectBooleanInput
} from '@lightbeamhealth/blueprint-form';
import { Row, Col } from '../../../../grid';
import { StartDate } from '../date';
import { EndDate } from '../date';
import SelectMethod from '../method';
import SelectLanguage from '../language';
import SelectTime from '../time';
import Weekday from '../weekday';
import Timezone from '../timezone';
import ScheduleRepeat from '../repeat';
import { CardBlock, CardBreakout } from '../../card';
import Callout from '../../callout';
import {
  GetCurrentTZ,
  GetFormalDate,
  ShorthandDate
} from '../../../../libs/time';
import { useMetaData } from '../../../../contexts/metadata';

interface EpxCOPDData {
  method: string;
  language: string;
  start: string;
  end?: string;
  tz: string;
  isFixed: boolean;
  mode?: string;
  times: string[];
  days: number[];
  step?: number;
  shouldAskSymptoms?: boolean;
  askSputumAppearanceChange?: boolean;
  shouldAskNebulizer?: boolean;
  shouldAskFever?: boolean;
  isBinary?: boolean;
  isConnectEnabled?: boolean;
}

type EpxCOPDDataRaw = DateSerialized<EpxCOPDData, 'start' | 'end'>;

export const EpxCOPDDefaultData: EpxCOPDData = {
  method: 'message',
  language: 'en',
  start: GetFormalDate(ShorthandDate.Today),
  end: null,
  tz: GetCurrentTZ(),
  isFixed: true,
  mode: 'W',
  times: ['10:00'],
  days: [1, 4],
  step: 1,
  shouldAskSymptoms: false,
  askSputumAppearanceChange: false,
  shouldAskNebulizer: false,
  shouldAskFever: false,
  isBinary: false,
  isConnectEnabled: false
};

const DATE_FORMAT = 'YYYY-MM-DD';
const { DeserializeDate, SerializeDate } = FormInstance;

interface EpxCOPDFormProps {
  setForm?: (form: FormInstance<EpxCOPDDataRaw>) => void;
  onSubmit?: (data: EpxCOPDData) => void;
  isDisabled?: boolean;
}

const EpxCOPDForm = ({
  setForm,
  onSubmit: _onSubmit,
  isDisabled
}: EpxCOPDFormProps): JSX.Element => {
  const { terms } = useMetaData();
  const serialize = (data: EpxCOPDDataRaw): EpxCOPDData => {
    return {
      ...data,
      start: SerializeDate(data.start, DATE_FORMAT),
      end: SerializeDate(data.end, DATE_FORMAT),
      times: data.times.slice(0, 1)
    };
  };
  const deserialize = (data: EpxCOPDData): EpxCOPDDataRaw => {
    return {
      ...data,
      start: DeserializeDate(data.start, DATE_FORMAT),
      end: DeserializeDate(data.end, DATE_FORMAT)
    };
  };
  const defaultData = deserialize(EpxCOPDDefaultData);
  const form = new FormInstance<EpxCOPDDataRaw, EpxCOPDData>(defaultData, {
    serialize,
    deserialize
  });
  useEffect(() => {
    if (setForm) {
      setForm(form);
    }
  }, []);
  const onChange = (data: EpxCOPDData): void => {
    if (!data.isFixed && data.mode !== 'W') {
      data.mode = 'W';
      form.setData(data);
    }
    if (data.isFixed) {
      if (data.step == null) {
        data.step = 1;
      }
      if (data.mode == null) {
        data.mode = 'W';
      }
      form.setData(data);
    }
  };
  const onSubmit = (data: EpxCOPDData): void => {
    _onSubmit(data);
  };
  return (
    <CardBlock bare>
      <Form
        form={form}
        onChange={onChange}
        onSubmit={onSubmit}
        disabled={isDisabled}
      >
        {(props) => {
          return (
            <>
              <CardBlock>
                <Row>
                  <Col sm={12}>
                    <SelectMethod />
                  </Col>
                  <Col sm={12}>
                    <SelectLanguage />
                  </Col>
                </Row>
              </CardBlock>
              <CardBlock>
                <Row>
                  <Col sm={12}>
                    <SelectBooleanInput
                      label="Schedule Type"
                      name="isFixed"
                      trueLabel="Fixed Schedule"
                      falseLabel="SMART Schedule"
                      fill
                    />
                  </Col>
                </Row>
              </CardBlock>
              <CardBlock>
                <Row>
                  <Col sm={12}>
                    <StartDate label="Start Date" name="start" fill />
                  </Col>
                  <Col sm={12}>
                    <EndDate label="End Date" name="end" />
                  </Col>
                  <Col sm={12}>
                    <SelectTime label="Message At" name="times[0]" />
                  </Col>
                  <Col sm={12}>
                    <Timezone label="Timezone" name="tz" fill />
                  </Col>
                </Row>
              </CardBlock>
              {props.values.isFixed ? (
                <>
                  <CardBlock>
                    <ScheduleRepeat label="Repeat Messages" />
                  </CardBlock>
                  {props.values.mode === 'W' ? (
                    <CardBreakout>
                      <Weekday name="days" inline />
                    </CardBreakout>
                  ) : null}
                </>
              ) : null}
              <CardBlock>
                <Row>
                  <Col sm={12}>
                    <SelectBooleanInput
                      label="Ask about Nebulizer Usage"
                      name="shouldAskNebulizer"
                      fill
                    />
                    <Callout>
                      Saying "Yes" to this adds a question about whether the{' '}
                      {terms.patient.default} has used a rescue inhaler or
                      nebulizer since the last check-in.
                    </Callout>
                  </Col>
                  <Col sm={12}>
                    <SelectBooleanInput
                      label="Ask about Fever"
                      name="shouldAskFever"
                      fill
                    />
                    <Callout>
                      Saying "Yes" to this asks about fever or chills when the{' '}
                      {terms.patient.default} reports a worse COPD symptom.
                    </Callout>
                  </Col>
                  <Col sm={12}>
                    <SelectBooleanInput
                      label="Ask about Symptoms"
                      name="shouldAskSymptoms"
                      fill
                    />
                    <Callout>
                      Saying "Yes" to this asks follow-up questions about cough
                      and sputum after the breathing question, and alerts on the
                      specific symptoms reported.
                    </Callout>
                  </Col>
                  <Col sm={12}>
                    <SelectBooleanInput
                      label="Ask Sputum Appearance Change"
                      name="askSputumAppearanceChange"
                      fill
                    />
                    <Callout>
                      Saying "Yes" to this replaces the standard sputum question
                      with a yes/no question about whether the{' '}
                      {terms.patient.default}'s sputum is darker, thicker, or
                      more discolored compared to the last check-in. A "Yes"
                      response will trigger an alert. The "Ask about Symptoms"
                      flag must also be on for this sputum question to be asked.
                    </Callout>
                  </Col>
                </Row>
              </CardBlock>
              <CardBlock>
                <Row>
                  <Col sm={24}>
                    <SelectBooleanInput
                      label="Binary Mode"
                      name="isBinary"
                      fill
                    />
                    <Callout>
                      Saying "Yes" to this changes the COPD symptom questions
                      from better/worse/same choices to simpler yes/no prompts.
                    </Callout>
                  </Col>
                </Row>
              </CardBlock>
              <CardBlock>
                <Row>
                  <Col sm={24}>
                    <SelectBooleanInput
                      label="Enable Text-in CONNECT"
                      name="isConnectEnabled"
                      fill
                    />
                    <Callout>
                      Saying "Yes" to this means the {terms.patient.default}{' '}
                      will be able to text "CONNECT" to the program phone number
                      in order to proactively send in responses at any time.
                    </Callout>
                  </Col>
                </Row>
              </CardBlock>
            </>
          );
        }}
      </Form>
    </CardBlock>
  );
};

export default EpxCOPDForm;
